import React from "react";
import type {
  RenderLayoutListNode as RenderLayoutListNodeModel,
  RenderNode,
  RuntimePatch,
  RuntimeTimedFlowStep,
} from "../../../util/src/layout-compiler/types.js";
import { resolveLayoutListRowOffsets } from "../../../util/src/layout/layout-list-rows.js";
import type { LayoutListDirection } from "../../../util/src/layout/types.js";
import { RenderNodeView } from "./RenderNodeView.js";
import {
  useRenderNodeByKey,
  useRuntimeCanvasElement,
  useRuntimeElementByKey,
} from "./CompiledLayoutRuntimeContext.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { getSiblingRenderReactKey } from "./renderReactKey.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";
import { playCompiledAnimationEffects } from "./playCompiledAnimationEffects.js";

type RenderListNodeProps = {
  node: RenderLayoutListNodeModel;
  scale: number;
};

function getNodeKey(node: RenderNode): string {
  return node.key ?? node.id;
}

function getNodeX(node: RenderNode): number {
  return Number.isFinite(node.x) ? node.x : 0;
}

function getNodeY(node: RenderNode): number {
  return Number.isFinite(node.y) ? node.y : 0;
}

const toNumberLength = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number.parseFloat(value.replace("px", "").trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const getMarginInsetsFromStyle = (
  style: Record<string, unknown>,
): {
  horizontal: number;
  vertical: number;
} => {
  const baseMargin = toNumberLength(style.margin);
  const top = toNumberLength(style.marginTop ?? baseMargin);
  const right = toNumberLength(style.marginRight ?? baseMargin);
  const bottom = toNumberLength(style.marginBottom ?? baseMargin);
  const left = toNumberLength(style.marginLeft ?? baseMargin);
  return {
    horizontal: left + right,
    vertical: top + bottom,
  };
};

const getChildOuterDimensions = (
  child: RenderNode,
): { outerWidth: number; outerHeight: number } => {
  const width = Number.isFinite(child.width) ? child.width : 0;
  const height = Number.isFinite(child.height) ? child.height : 0;
  const margins = getMarginInsetsFromStyle(child.style ?? {});
  return {
    outerWidth: Math.max(0, width + margins.horizontal),
    outerHeight: Math.max(0, height + margins.vertical),
  };
};

/**
 * Derives the list shell size from rendered row children, matching compile-time
 * stackedWidth/stackedHeight but based on the rows actually on screen.
 */
function computeStackedSizeFromRenderedChildren(
  children: RenderNode[],
  direction: LayoutListDirection = "column",
): { width: number; height: number } {
  if (children.length === 0) {
    return { width: 0, height: 0 };
  }

  const positioned = resolveLayoutListRowOffsets(
    children.map((child) => getChildOuterDimensions(child)),
    direction,
  );

  const width = positioned.reduce(
    (max, row) => Math.max(max, row.offsetX + row.outerWidth),
    0,
  );
  const height = positioned.reduce(
    (max, row) => Math.max(max, row.offsetY + row.outerHeight),
    0,
  );

  return { width, height };
}

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) {
    return `${rotationTransform} ${extra}`;
  }

  return rotationTransform ?? extra;
}

/**
 * Renders a compiled layout list: absolute shell with pre-positioned row children.
 */
export function RenderListNode({ node, scale }: RenderListNodeProps) {
  if (node.hasListFilterTransitionPresence) {
    return <RenderListNodeWithFilterPresence node={node} scale={scale} />;
  }
  return <RenderListNodeStatic node={node} scale={scale} />;
}

function buildListStyle(
  node: RenderLayoutListNodeModel,
  scale: number,
  contentSize?: { width: number; height: number },
): React.CSSProperties {
  const width = contentSize?.width ?? node.width;
  // An explicitly scrollable list keeps the editor's viewport height. Other
  // lists grow with their rows, including documents prepared for printing.
  const scrollable =
    node.style.overflow === "auto" || node.style.overflow === "scroll";
  const height = scrollable
    ? node.height
    : (contentSize?.height ?? node.height);
  const clipStyle = getRenderNodeClipStyle(node, scale);
  return {
    ...(node.style.position === "relative"
      ? {
          position: "relative",
          flexShrink: 0,
        }
      : {
          position: "absolute",
          left: node.x * scale,
          top: node.y * scale,
        }),
    width: width * scale,
    height: height * scale,
    zIndex: node.zIndex,
    boxSizing: "border-box",
    overflow: "hidden",
    ...(node.style as React.CSSProperties),
    transform: composeTransform(node.rotation, node.style?.transform),
    transformOrigin: "center center",
    ...clipStyle,
  };
}

function useDirectChildTopLeftFlip(params: {
  enabled: boolean;
  children: RenderNode[];
  runtimeElementByKey: Map<string, HTMLElement> | undefined;
  scale: number;
}) {
  const previousOffsetsRef = React.useRef<
    Map<string, { x: number; y: number }>
  >(new Map());
  React.useLayoutEffect(() => {
    const nextOffsets = new Map<string, { x: number; y: number }>();
    for (const child of params.children) {
      nextOffsets.set(getNodeKey(child), {
        x: getNodeX(child),
        y: getNodeY(child),
      });
    }
    if (
      !params.enabled ||
      !params.runtimeElementByKey ||
      params.children.length === 0
    ) {
      previousOffsetsRef.current = nextOffsets;
      return;
    }
    const previousOffsets = previousOffsetsRef.current;
    const movedElements: Array<{
      element: HTMLElement;
      nextX: number;
      nextY: number;
      previousInlineTransition: string;
    }> = [];
    for (const child of params.children) {
      if (!child.hasTopLeftTransition) continue;
      const childKey = getNodeKey(child);
      const previousOffset = previousOffsets.get(childKey);
      if (!previousOffset) continue;
      const nextX = getNodeX(child);
      const nextY = getNodeY(child);
      if (previousOffset.x === nextX && previousOffset.y === nextY) continue;
      const element = params.runtimeElementByKey.get(childKey);
      if (!element) continue;
      movedElements.push({
        element,
        nextX,
        nextY,
        previousInlineTransition: element.style.transition,
      });
      element.style.transition = "none";
      element.style.left = `${previousOffset.x * params.scale}px`;
      element.style.top = `${previousOffset.y * params.scale}px`;
    }
    if (movedElements.length > 0) {
      void movedElements[0]!.element.offsetHeight;
      for (const moved of movedElements) {
        moved.element.style.transition = moved.previousInlineTransition;
        moved.element.style.left = `${moved.nextX * params.scale}px`;
        moved.element.style.top = `${moved.nextY * params.scale}px`;
      }
    }
    previousOffsetsRef.current = nextOffsets;
  }, [
    params.children,
    params.enabled,
    params.runtimeElementByKey,
    params.scale,
  ]);
}

function RenderListNodeStatic({ node, scale }: RenderListNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const runtimeElementByKey = useRuntimeElementByKey();
  useDirectChildTopLeftFlip({
    enabled: Boolean(node.requiresDirectChildTopLeftFlip),
    children: node.children,
    runtimeElementByKey,
    scale,
  });
  const listStyle = buildListStyle(node, scale);
  return (
    <div {...runtimeProps} style={listStyle}>
      {node.children.map((child, index) => (
        <RenderNodeView
          key={getSiblingRenderReactKey(child, index, node.children)}
          node={child}
          scale={scale}
        />
      ))}
    </div>
  );
}

type FilterTransitionPhase = "idle" | "exiting" | "entering";
type DisplayedListSnapshot = {
  filterKey: string;
  children: RenderNode[];
};

function patchToLocalStyle(patch: RuntimePatch): React.CSSProperties {
  if (patch.key === "visibility") {
    if (patch.value === false) {
      return { visibility: "hidden" };
    }
    if (patch.value === true) {
      return { visibility: "visible" };
    }
  }

  return { [patch.key]: patch.value } as React.CSSProperties;
}

function RenderListNodeWithFilterPresence({
  node,
  scale,
}: RenderListNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const runtimeElementByKey = useRuntimeElementByKey();
  const renderNodeByKey = useRenderNodeByKey();
  const canvasElement = useRuntimeCanvasElement();
  const currentFilterKey = node.listFilterKey ?? "__default__";
  const [displayedSnapshot, setDisplayedSnapshot] =
    React.useState<DisplayedListSnapshot>(() => ({
      filterKey: currentFilterKey,
      children: node.children,
    }));
  const displayedSnapshotRef = React.useRef(displayedSnapshot);
  displayedSnapshotRef.current = displayedSnapshot;
  const pendingSnapshotRef = React.useRef<DisplayedListSnapshot | null>(null);
  const phaseRef = React.useRef<FilterTransitionPhase>("idle");
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const stepTimeoutsRef = React.useRef<ReturnType<typeof setTimeout>[]>([]);
  const [transitionStyle, setTransitionStyle] =
    React.useState<React.CSSProperties>({});

  const clearTransitionTimeout = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    for (const timeout of stepTimeoutsRef.current) {
      clearTimeout(timeout);
    }
    stepTimeoutsRef.current = [];
  }, []);

  const runTransitionStep = React.useCallback(
    (_kind: "exit" | "enter", step: RuntimeTimedFlowStep) => {
      if (step.patches.length > 0) {
        setTransitionStyle((current) => ({
          ...current,
          ...Object.assign({}, ...step.patches.map(patchToLocalStyle)),
        }));
      }

      playCompiledAnimationEffects(step.effects, {
        runtimeElementByKey:
          runtimeElementByKey ?? new Map<string, HTMLElement>(),
        renderNodeByKey,
        canvasElement,
        scale,
      });
    },
    [runtimeElementByKey, renderNodeByKey, canvasElement, scale],
  );

  const scheduleTransitionSteps = React.useCallback(
    (kind: "exit" | "enter") => {
      const steps =
        kind === "exit"
          ? (node.listFilterExitSteps ?? [
              {
                atMs: 0,
                patches: [],
                effects: node.listFilterExitEffects ?? [],
              },
            ])
          : (node.listFilterEnterSteps ?? [
              {
                atMs: 0,
                patches: [],
                effects: node.listFilterEnterEffects ?? [],
              },
            ]);

      for (const step of steps) {
        const atMs = Math.max(0, step.atMs);
        if (atMs <= 0) {
          runTransitionStep(kind, step);
          continue;
        }

        const timeout = setTimeout(() => {
          runTransitionStep(kind, step);
        }, atMs);
        stepTimeoutsRef.current.push(timeout);
      }
    },
    [
      node.listFilterEnterEffects,
      node.listFilterEnterSteps,
      node.listFilterExitEffects,
      node.listFilterExitSteps,
      runTransitionStep,
    ],
  );

  const startFilterExitThenEnter = React.useCallback(() => {
    clearTransitionTimeout();
    phaseRef.current = "exiting";
    scheduleTransitionSteps("exit");
    const exitWaitMs = Math.max(0, node.listFilterExitDurationMs ?? 0);
    timeoutRef.current = setTimeout(() => {
      const pendingSnapshot = pendingSnapshotRef.current;
      if (!pendingSnapshot) {
        phaseRef.current = "idle";
        timeoutRef.current = null;
        return;
      }
      setDisplayedSnapshot(pendingSnapshot);
      displayedSnapshotRef.current = pendingSnapshot;
      pendingSnapshotRef.current = null;
      phaseRef.current = "entering";
      requestAnimationFrame(() => {
        scheduleTransitionSteps("enter");
        const enterWaitMs = Math.max(0, node.listFilterEnterDurationMs ?? 0);
        timeoutRef.current = setTimeout(() => {
          phaseRef.current = "idle";
          timeoutRef.current = null;
        }, enterWaitMs);
      });
    }, exitWaitMs);
  }, [
    clearTransitionTimeout,
    node.id,
    node.listFilterEnterDurationMs,
    node.listFilterExitDurationMs,
    node.listFilterExitEffects,
    scheduleTransitionSteps,
  ]);

  React.useLayoutEffect(() => {
    const nextSnapshot: DisplayedListSnapshot = {
      filterKey: currentFilterKey,
      children: node.children,
    };
    const currentSnapshot = displayedSnapshotRef.current;
    const filterKeyChanged =
      currentSnapshot.filterKey !== nextSnapshot.filterKey;
    const hasExitSteps =
      (node.listFilterExitSteps?.length ??
        node.listFilterExitEffects?.length ??
        0) > 0;
    const exitWaitMs = Math.max(0, node.listFilterExitDurationMs ?? 0);
    const isExiting = phaseRef.current === "exiting";
    if (!filterKeyChanged) {
      if (phaseRef.current === "idle") {
        setDisplayedSnapshot(nextSnapshot);
        displayedSnapshotRef.current = nextSnapshot;
      } else {
        pendingSnapshotRef.current = nextSnapshot;
      }
      return;
    }
    pendingSnapshotRef.current = nextSnapshot;
    if (!hasExitSteps || exitWaitMs <= 0) {
      setDisplayedSnapshot(nextSnapshot);
      displayedSnapshotRef.current = nextSnapshot;
      pendingSnapshotRef.current = null;
      if (
        (node.listFilterEnterSteps?.length ??
          node.listFilterEnterEffects?.length ??
          0) > 0
      ) {
        requestAnimationFrame(() => {
          scheduleTransitionSteps("enter");
        });
      }
      return;
    }
    if (!isExiting) {
      startFilterExitThenEnter();
    }
  }, [
    node.id,
    node.children,
    currentFilterKey,
    node.hasListFilterTransitionPresence,
    node.listFilterEnterEffects,
    node.listFilterEnterSteps,
    node.listFilterExitDurationMs,
    node.listFilterExitEffects,
    node.listFilterExitSteps,
    scheduleTransitionSteps,
    startFilterExitThenEnter,
  ]);

  React.useEffect(
    () => () => {
      clearTransitionTimeout();
    },
    [clearTransitionTimeout],
  );

  const shouldRenderCurrentChildren =
    phaseRef.current === "idle" &&
    displayedSnapshot.filterKey === currentFilterKey;
  const renderedChildren = shouldRenderCurrentChildren
    ? node.children
    : displayedSnapshot.children;

  useDirectChildTopLeftFlip({
    enabled:
      Boolean(node.requiresDirectChildTopLeftFlip) &&
      phaseRef.current === "idle",
    children: renderedChildren,
    runtimeElementByKey,
    scale,
  });
  const listStyle = {
    ...buildListStyle(
      node,
      scale,
      computeStackedSizeFromRenderedChildren(
        renderedChildren,
        node.direction ?? "column",
      ),
    ),
    ...transitionStyle,
  };

  return (
    <div {...runtimeProps} style={listStyle}>
      {renderedChildren.map((child, index) => (
        <RenderNodeView
          key={getSiblingRenderReactKey(child, index, renderedChildren)}
          node={child}
          scale={scale}
        />
      ))}
    </div>
  );
}
